import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { PieChart, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(4)}%`);

export default function CapTablePreviewCard({ companyId }) {
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/shareholders`, { params: { company_id: companyId } })
      .then((res) => setShareholders(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  const sorted = [...shareholders].sort((a, b) => (b.ownership_pct || 0) - (a.ownership_pct || 0));
  const totalFounder = sorted.reduce((s, sh) => s + (sh.founder_shares || 0), 0);
  const totalPreferred = sorted.reduce((s, sh) => s + (sh.preferred_shares || 0), 0);
  const totalCommon = sorted.reduce((s, sh) => s + (sh.shares || 0), 0);
  const grandTotal = totalFounder + totalPreferred + totalCommon;
  const totalPct = sorted.reduce((s, sh) => s + (sh.ownership_pct || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="cap-table-preview">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PieChart className="w-4 h-4 text-[#1E40AF]" />
          <h2 className="font-medium text-gray-900">Cap Table Overview</h2>
        </div>
        <Link to="/shareholders" className="text-sm text-[#1E40AF] hover:text-[#1E3A8A] inline-flex items-center gap-1" data-testid="view-full-captable">
          View full cap table <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {loading ? (
        <div className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : sorted.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">No shareholders yet. <Link to="/shareholders" className="text-[#1E40AF] hover:underline">Add one</Link>.</div>
      ) : (
        <>
          {/* Summary bar */}
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 grid grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Members:</span> <span className="font-semibold text-gray-900 ml-1">{sorted.length}</span></div>
            <div><span className="text-purple-600">Founders:</span> <span className="font-semibold text-gray-900 ml-1">{totalFounder.toLocaleString()}</span></div>
            <div><span className="text-amber-600">Series A:</span> <span className="font-semibold text-gray-900 ml-1">{totalPreferred.toLocaleString()}</span></div>
            <div><span className="text-blue-600">Employee:</span> <span className="font-semibold text-gray-900 ml-1">{totalCommon.toLocaleString()}</span></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-purple-600 uppercase">Founders</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-amber-600 uppercase">Series A</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-blue-600 uppercase">Employee</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Ownership %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((sh) => {
                  const f = sh.founder_shares || 0;
                  const p = sh.preferred_shares || 0;
                  const c = sh.shares || 0;
                  const tot = f + p + c;
                  return (
                    <tr key={sh.id} className="hover:bg-gray-50">
                      <td className="px-5 py-2 text-sm font-medium text-gray-900">{sh.name}</td>
                      <td className="px-3 py-2 text-right text-sm">{f > 0 ? <span className="text-purple-700">{f.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-sm">{p > 0 ? <span className="text-amber-700">{p.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-sm">{c > 0 ? <span className="text-blue-700">{c.toLocaleString()}</span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{tot.toLocaleString()}</td>
                      <td className="px-5 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#1E40AF] rounded-full" style={{ width: `${Math.min(100, sh.ownership_pct || 0)}%` }} /></div>
                          <span className="text-sm font-semibold text-gray-900 w-20 text-right">{fmtPct(sh.ownership_pct)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-5 py-2.5 text-sm text-gray-900">Totals</td>
                  <td className="px-3 py-2.5 text-right text-sm text-purple-700">{totalFounder.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right text-sm text-amber-700">{totalPreferred.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right text-sm text-blue-700">{totalCommon.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right text-sm text-gray-900">{grandTotal.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right text-sm text-gray-900">{totalPct.toFixed(4)}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
